import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PLATFORMS } from "@/lib/platforms";
import { useAuth } from "@/context/AuthContext";

const DECISIONS = [
  {
    n: "01",
    t: "Arranged the way people live",
    body: "So we arranged health the way people actually live it. Seven places, named after what you feel rather than what a clinic calls it. Something feels wrong in my body. My head is loud. I want to get strong. My nights are broken. It is about my stage of life. The people around me. I want to stay ahead of it.",
  },
  {
    n: "02",
    t: "Anonymous, and not optionally",
    body: "Where we are from, asking about your own health has witnesses. The neighbour at the clinic. The cousin who works at the pharmacy. So here you are an alias and a colour, and even the specialists who answer you under their real names will never learn yours.",
  },
  {
    n: "03",
    t: "Free, with nothing behind it",
    body: "Nothing here costs anything, and nothing ever will. There is no pricing page to find. There is not going to be one.",
  },
];

const rise = (d = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export default function Why() {
  const { user, setEntryOpen } = useAuth();
  return (
    <main className="pt-16" data-testid="why-page">
      {/* ---- the opening ---- */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 right-[-10%] w-[640px] h-[640px] opacity-70"
          style={{ background: "radial-gradient(circle, rgba(239,234,252,0.95), transparent 66%)" }} />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-4">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <p className="kicker text-[var(--ink-soft)]">Why this exists</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.04]">
              Written by people,<br /><span className="text-[var(--ink-soft)]">not a company</span>
            </h1>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <motion.div className="lg:col-span-7" {...rise(0.05)}>
              <p className="font-display text-2xl sm:text-[28px] leading-[1.34]">
                Health is split into buildings. Cardiology on the third floor, psychiatry across town, the gym in a different world entirely, and sleep belonging to nobody.
                <span className="block mt-2" style={{ color: "var(--ink-soft)" }}>A person is not split that way.</span>
              </p>
            </motion.div>
            <motion.div className="lg:col-span-5 lg:pt-3" {...rise(0.14)}>
              <div className="border-l-2 pl-5" style={{ borderColor: "var(--line)" }}>
                <p className="text-[var(--ink-soft)] leading-relaxed">
                  A bad month rarely lives in one specialty. The nights break, then the food goes, then the temper, then the body starts sending letters. Ask any doctor where that person should go first and you will get an honest shrug.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- the three decisions ---- */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-4">
        <p className="kicker text-[var(--ink-soft)]">Three decisions, made early</p>
        <div className="mt-7 space-y-4">
          {DECISIONS.map((d, i) => (
            <motion.article
              key={d.n}
              {...rise(i * 0.06)}
              className={`relative rounded-[1.8rem] border border-[var(--line)] bg-white/50 p-7 sm:p-8 ${i === 1 ? "lg:ml-14" : i === 2 ? "lg:ml-7" : ""}`}
            >
              <div className="flex items-baseline gap-4">
                <span className="font-mono-nc text-xs text-[var(--ink-soft)] shrink-0">{d.n}</span>
                <h2 className="font-display text-2xl leading-snug">{d.t}</h2>
              </div>
              <p className="mt-3.5 text-[var(--ink-soft)] leading-relaxed sm:pl-10">{d.body}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* ---- the name ---- */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 pb-6">
        <motion.div {...rise()} className="relative rounded-[2rem] p-9 sm:p-12 overflow-hidden"
          style={{ background: "linear-gradient(140deg, #2A2338 0%, #3A2E52 55%, #241D30 100%)" }}>
          <div className="pointer-events-none absolute -right-16 -top-20 w-[380px] h-[380px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(232,184,75,0.22), transparent 68%)" }} />
          <p className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: "rgba(255,255,255,0.4)" }}>The name</p>
          <p className="relative font-display text-[26px] sm:text-[34px] leading-[1.28] text-white mt-4 max-w-2xl">
            Novcovek means new person. Not a better one, not an optimised one.
            <span className="block mt-2" style={{ color: "#E8B84B" }}>Just the one who finally knows where to look.</span>
          </p>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-3">
          {!user && (
            <button onClick={() => setEntryOpen(true)} className="btn-ink rounded-full px-6 py-3 font-semibold" data-testid="why-alias-btn">
              Pick your alias
            </button>
          )}
          <Link to="/" className="btn-line rounded-full px-6 py-3 font-semibold" data-testid="why-home-btn">
            See the seven
          </Link>
        </div>

        <div className="mt-12">
          <p className="kicker text-[var(--ink-soft)] mb-3">Go straight to one</p>
          <div className="flex gap-2">
            {PLATFORMS.map((p) => (
              <Link key={p.key} to={p.path} title={p.name} className="group flex-1" aria-label={p.name}>
                <span className="block h-2.5 rounded-full transition-transform group-hover:scale-y-[1.8]" style={{ background: p.hue }} />
                <span className="mt-2 block text-[10px] text-[var(--ink-soft)] opacity-0 group-hover:opacity-100 transition-opacity truncate">{p.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
